import { FormProvider, getFormProps } from '@conform-to/react'
import { Button } from '@radix-ui/themes'
import { useLocation } from '@remix-run/react'
import * as React from 'react'
import { RxPaperPlane } from 'react-icons/rx'
import { z } from 'zod'
import { FormDefaults } from '#app/components/common/form-defaults.tsx'
import { SubmissionButton } from '#app/components/common/submission-button.tsx'
import { type Locale } from '#app/models/locale.ts'
import { type FormEnum } from '#app/routes/_layout/route-config.ts'
import { schemaDefault } from '#app/utils/form-defaults.ts'
import { useResendVerificationEmailForm } from '#app/utils/hooks/forms/use-resend-verification-email-form.ts'

/* -------------------------------------------------------------------------------------------------
 * Resend verification form
 * -----------------------------------------------------------------------------------------------*/

const FORM: FormEnum = {
	id: 'profile-resend-verification-email-form',
	action: '/profile',
	fetcherKey: 'profile-resend-verification-email',
	subaction: 'profile-resend-verification-email',
	redirect: '/profile',
} as const

const schema = z.object({
	...schemaDefault,
})

type ResendVerificationEmailFormProps = {
	locale: Locale
}

const ResendVerificationEmailForm: React.FC<
	ResendVerificationEmailFormProps
> = ({ locale }) => {
	const {
		resendVerificationEmailFetcher,
		resendVerificationEmailFormValues,
		resendVerificationEmailForm,
		resendVerificationEmailFields,
		t,
	} = useResendVerificationEmailForm()

	const location = useLocation()

	return (
		<FormProvider context={resendVerificationEmailForm.context}>
			<resendVerificationEmailFetcher.Form
				method="post"
				action={`/${locale}${FORM.action}${location.search}`}
				{...getFormProps(resendVerificationEmailForm)}
			>
				<FormDefaults
					form={resendVerificationEmailForm}
					fields={resendVerificationEmailFields}
					values={resendVerificationEmailFormValues}
				/>
				<Button variant="ghost" type="submit">
					<SubmissionButton fetcher={resendVerificationEmailFetcher}>
						<RxPaperPlane />
						{t('button.resendVerificationEmailSubmit')}
					</SubmissionButton>
				</Button>
			</resendVerificationEmailFetcher.Form>
		</FormProvider>
	)
}
ResendVerificationEmailForm.displayName = 'ResendVerificationEmailForm'

/* -----------------------------------------------------------------------------------------------*/

export {
	type ResendVerificationEmailFormProps,
	ResendVerificationEmailForm,
	FORM as RESEND_VERIFICATION_EMAIL_FORM,
	schema as resendVerificationEmailSchema,
}
